import config from '../../config';
import { getAuthHeaders, getAuthHeaderOnly, handleResponse, handleNoContentResponse } from './common';

export const PRACTICE_DOCUMENT_TYPES = {
    LEASE_AGREEMENT: 'LeaseAgreement',
    INSURANCE:       'Insurance',
    LICENSE:         'License',
    FINANCIAL:       'Financial',
    LEGAL:           'Legal',
    OTHER:           'Other',
};

export const PRACTICE_DOCUMENT_TYPE_LABELS = {
    [PRACTICE_DOCUMENT_TYPES.LEASE_AGREEMENT]: 'Lease Agreement',
    [PRACTICE_DOCUMENT_TYPES.INSURANCE]:       'Insurance',
    [PRACTICE_DOCUMENT_TYPES.LICENSE]:         'License',
    [PRACTICE_DOCUMENT_TYPES.FINANCIAL]:       'Financial',
    [PRACTICE_DOCUMENT_TYPES.LEGAL]:           'Legal',
    [PRACTICE_DOCUMENT_TYPES.OTHER]:           'Other',
};

function normalizePracticeDocument(dto) {
    if (!dto) return dto;
    return {
        referenceId:   dto.referenceId   ?? dto.ReferenceId,
        fileName:      dto.fileName      ?? dto.FileName,
        documentType:  dto.documentType  ?? dto.DocumentType  ?? PRACTICE_DOCUMENT_TYPES.OTHER,
        description:   dto.description   ?? dto.Description   ?? null,
        contentType:   dto.contentType   ?? dto.ContentType   ?? null,
        fileSizeBytes: dto.fileSizeBytes ?? dto.FileSizeBytes ?? null,
        uploadedBy:    dto.uploadedBy    ?? dto.UploadedBy    ?? null,
        uploadedAt:    dto.uploadedAt    ?? dto.UploadedAt,
    };
}

const practiceDocumentsApi = {
    /**
     * List all uploaded documents for an organization.
     * GET /organizations/{orgReferenceId}/documents
     */
    listDocuments: async (orgReferenceId, params = {}) => {
        const query = new URLSearchParams();
        if (params.documentType) query.append('documentType', params.documentType);

        const qs = query.toString();
        const url = `${config.api.url}/organizations/${orgReferenceId}/documents${qs ? `?${qs}` : ''}`;
        const res = await fetch(url, { headers: getAuthHeaders() });
        const result = await handleResponse(res);

        if (Array.isArray(result)) return result.map(normalizePracticeDocument);
        if (result && Array.isArray(result.items)) return result.items.map(normalizePracticeDocument);
        if (result && Array.isArray(result.$values)) return result.$values.map(normalizePracticeDocument);
        return result;
    },

    /**
     * Upload a new document for an organization.
     * POST /organizations/{orgReferenceId}/documents  (multipart/form-data)
     */
    uploadDocument: async (orgReferenceId, file, options = {}) => {
        const form = new FormData();
        form.append('file', file);
        form.append('documentType', options.documentType ?? PRACTICE_DOCUMENT_TYPES.OTHER);
        if (options.description) form.append('description', options.description);

        const url = `${config.api.url}/organizations/${orgReferenceId}/documents`;
        const res = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaderOnly(),
            body: form,
        });
        const result = await handleResponse(res);
        return normalizePracticeDocument(result);
    },

    /**
     * Get a short-lived URL to view/download a document.
     * GET /organizations/{orgReferenceId}/documents/{docReferenceId}/url
     */
    getDocumentUrl: async (orgReferenceId, docReferenceId) => {
        const url = `${config.api.url}/organizations/${orgReferenceId}/documents/${docReferenceId}/url`;
        const res = await fetch(url, { headers: getAuthHeaders() });
        return handleResponse(res);
    },

    /**
     * Delete an uploaded document.
     * DELETE /organizations/{orgReferenceId}/documents/{docReferenceId}
     */
    deleteDocument: async (orgReferenceId, docReferenceId) => {
        const url = `${config.api.url}/organizations/${orgReferenceId}/documents/${docReferenceId}`;
        const res = await fetch(url, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        return handleNoContentResponse(res);
    },
};

export default practiceDocumentsApi;
